import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/AuthContext.js';
import { ApiClient } from '../lib/api.js';

function looksLikeUrl(s: string): boolean {
  const t = s.trim();
  if (!t || /\s/.test(t)) return false;
  return /^https?:\/\/\S+$/i.test(t);
}

/**
 * Pick tweet vs thread based on input shape — so the user doesn't have to decide.
 * - URL: usually shared as one punchy tweet, not a thread.
 * - Short input: tweet.
 * - Multi-paragraph / bulleted / numbered / long: thread.
 */
function autoDetectFormat(input: string): 'tweet' | 'thread' {
  const t = input.trim();
  if (looksLikeUrl(t)) return 'tweet';
  if (t.length < 240) return 'tweet';
  const paragraphs = t.split(/\n\s*\n/).filter(p => p.trim().length > 0);
  if (paragraphs.length >= 2) return 'thread';
  if (/^\s*[-*•]\s/m.test(t)) return 'thread';
  if (/^\s*\d+[.)]\s/m.test(t)) return 'thread';
  if (t.length > 500) return 'thread';
  return 'tweet';
}

interface ResultState {
  draftId: string;
  mode: 'tweet' | 'thread';
  variants: { label?: string; text: string }[];
  originals: string[];
  sourceReply: string | null;
  pageInfo: { title?: string; imageUrl?: string; host?: string } | null;
}

/**
 * Standalone /app/write page — keeps the URL working for back-compat and direct links.
 * Renders the page chrome + the reusable WritePanel.
 */
export function Write() {
  return (
    <div className="page">
      <div className="page-head">
        <h1>Write</h1>
        <p className="lede">Paste a URL or a brief — anything from a one-line topic to a full marketing doc. We'll draft it in your voice.</p>
      </div>
      <WritePanel />
    </div>
  );
}

/**
 * The actual Write UI without page chrome. Used by Home (tabbed) and Write (page).
 */
export function WritePanel() {
  const { getToken } = useAuth();
  const api = new ApiClient(getToken);

  const [input, setInput] = useState('');
  const [busy, setBusy] = useState<'tweet' | 'thread' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);

  const isUrl = looksLikeUrl(input);
  const canGenerate = !busy && input.trim().length > 0;
  const detectedMode = canGenerate ? autoDetectFormat(input) : null;

  async function run(mode: 'tweet' | 'thread') {
    if (!canGenerate) return;
    setBusy(mode); setErr(null); setResult(null);
    try {
      if (isUrl) {
        const r = await api.draftFromUrl({ url: input.trim(), mode });
        const variants: { label?: string; text: string }[] = r.draft?.variants ?? [];
        const host = (() => { try { return new URL(r.page?.url ?? input).hostname.replace(/^www\./, ''); } catch { return undefined; } })();
        setResult({
          draftId: r.draft?.draftId ?? '',
          mode,
          variants,
          originals: variants.map(v => v.text),
          sourceReply: r.draft?.sourceReply ?? null,
          pageInfo: { title: r.page?.title, imageUrl: r.page?.imageUrl, host },
        });
      } else if (mode === 'thread') {
        const r = await api.generateThread(input.trim());
        const variants = r.posts.map((text, i) => ({ label: `post ${i + 1}`, text }));
        const saved = await api.saveDraft({ surface: 'web', mode: 'thread', variants, topic: input.trim() });
        setResult({
          draftId: saved.draft?.draftId ?? '',
          mode: 'thread',
          variants,
          originals: variants.map(v => v.text),
          sourceReply: null,
          pageInfo: null,
        });
      } else {
        const r = await api.generateTweet(input.trim());
        const variants = r.variants;
        const saved = await api.saveDraft({ surface: 'web', mode: 'tweet', variants, topic: input.trim() });
        setResult({
          draftId: saved.draft?.draftId ?? '',
          mode: 'tweet',
          variants,
          originals: variants.map(v => v.text),
          sourceReply: null,
          pageInfo: null,
        });
      }
    } catch (e: any) { setErr(e.message); }
    setBusy(null);
  }

  function updateVariantText(i: number, text: string) {
    setResult(prev => prev && {
      ...prev,
      variants: prev.variants.map((v, idx) => idx === i ? { ...v, text } : v),
    });
  }

  return (
    <>
      <div className="card">
        <div className="field">
          <label>Topic, brief, or URL</label>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={'Examples:\n· "AI agents for founders"\n· Paste your marketing brief, blog post, or product copy\n· https://techcrunch.com/some-article'}
            style={{ minHeight: 160 }}
            disabled={!!busy}
            onKeyDown={e => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && detectedMode) run(detectedMode);
            }}
          />
          {isUrl && (
            <p className="helper">Detected a URL — we'll fetch the page, extract the headline + image, and draft from it.</p>
          )}
        </div>

        <div className="button-row between">
          <span className="muted small">
            {detectedMode === 'thread' && 'Looks long — we\'ll draft a thread.'}
            {detectedMode === 'tweet' && isUrl && 'We\'ll draft a single tweet about this link.'}
            {detectedMode === 'tweet' && !isUrl && 'We\'ll draft a single tweet.'}
            {!detectedMode && '⌘+Enter to generate'}
          </span>
          <button
            className="primary"
            onClick={() => detectedMode && run(detectedMode)}
            disabled={!detectedMode}
          >
            {busy ? (busy === 'thread' ? 'Writing thread…' : 'Writing tweet…') : 'Generate'}
          </button>
        </div>
        {err && <div className="error">{err}</div>}
      </div>

      {result?.pageInfo && (
        <div className="card">
          <h4>From {result.pageInfo.host}</h4>
          <strong>{result.pageInfo.title}</strong>
          {result.pageInfo.imageUrl && (
            <img src={result.pageInfo.imageUrl} alt="" style={{ display: 'block', maxWidth: '100%', borderRadius: 8, marginTop: 8 }} />
          )}
        </div>
      )}

      {result && (
        <ImageCard
          api={api}
          ogImageUrl={result.pageInfo?.imageUrl}
          slug={(result.pageInfo?.title ?? input).trim().slice(0, 60)}
          topic={(result.pageInfo?.title ?? input).trim()}
        />
      )}

      {result && result.variants.length > 0 && (
        <div className="results">
          {result.variants.map((v, i) => (
            <VariantCard
              key={i}
              api={api}
              draftId={result.draftId}
              index={i}
              label={v.label}
              text={v.text}
              originalText={result.originals[i]}
              onChange={t => updateVariantText(i, t)}
            />
          ))}

          {result.sourceReply && (
            <div className="card variant" style={{ borderStyle: 'dashed' }}>
              <div className="label">Reply · source attribution</div>
              <p className="variant-text">{result.sourceReply}</p>
              <p className="helper">Post this as a reply to your first tweet. Keeps the hook tweet link-free (X demotes posts with outbound links).</p>
              <div className="button-row end">
                <button onClick={() => navigator.clipboard.writeText(result.sourceReply!)}>Copy</button>
                <button className="primary" onClick={() => window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(result.sourceReply!)}`, '_blank')}>Open reply composer →</button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Image card. Saves to ~/Downloads/tweetvia/ via the extension so the user can attach
 * it when posting on X. Three paths:
 *   1. If we have an og:image (URL drafts), one-click download.
 *   2. Always: paste any image URL — useful for text drafts where there's no og:image.
 *   3. Generate a cover image via Nano Banana (Gemini image gen). Offered automatically
 *      when there's no og:image; available on demand otherwise.
 */
function ImageCard({ api, ogImageUrl, slug, topic }: { api: ApiClient; ogImageUrl?: string; slug: string; topic: string }) {
  const [customUrl, setCustomUrl] = useState('');
  const [busy, setBusy] = useState<'og' | 'custom' | 'gen' | 'gen-dl' | null>(null);
  const [savedPath, setSavedPath] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [generated, setGenerated] = useState<string | null>(null);   // data URL

  async function download(url: string, kind: 'og' | 'custom' | 'gen-dl') {
    setBusy(kind); setErr(null); setSavedPath(null);
    try {
      const { downloadImageViaExtension, pingExtension } = await import('../lib/extensionBridge.js');
      const ping = await pingExtension();
      if (!ping.installed) {
        setErr('Install the tweetvia Chrome extension to save images. Browsers block cross-origin downloads from pages.');
        setBusy(null);
        return;
      }
      const path = await downloadImageViaExtension(url, slug);
      if (path) setSavedPath(path);
      else setErr("Couldn't download — the URL may be blocking external access.");
    } catch (e: any) { setErr(e.message); }
    setBusy(null);
  }

  async function generateCover() {
    if (!topic) { setErr('Need a topic to generate from.'); return; }
    setBusy('gen'); setErr(null); setSavedPath(null); setGenerated(null);
    try {
      const r = await api.generateImage(topic);
      setGenerated(r.dataUrl);
    } catch (e: any) { setErr(e.message); }
    setBusy(null);
  }

  return (
    <div className="card">
      <div className="button-row between" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Attach an image</h3>
        <span className="muted small">Saves to <code>~/Downloads/tweetvia/</code></span>
      </div>

      {ogImageUrl && (
        <div className="button-row between" style={{ marginBottom: 12 }}>
          <span className="muted small">Use the page's preview image</span>
          <button className="primary" onClick={() => download(ogImageUrl, 'og')} disabled={busy === 'og'}>
            {busy === 'og' ? 'Downloading…' : 'Download preview image'}
          </button>
        </div>
      )}

      {!ogImageUrl && !generated && (
        <div className="button-row between" style={{ marginBottom: 12 }}>
          <span className="muted small">No image found — generate a cover with AI (Nano Banana).</span>
          <button className="primary" onClick={generateCover} disabled={busy === 'gen'}>
            {busy === 'gen' ? 'Generating…' : 'Generate cover image'}
          </button>
        </div>
      )}

      {generated && (
        <div style={{ marginBottom: 12 }}>
          <img src={generated} alt="Generated cover" style={{ display: 'block', maxWidth: '100%', borderRadius: 8 }} />
          <div className="button-row end" style={{ marginTop: 8 }}>
            <button className="ghost" onClick={generateCover} disabled={busy === 'gen'}>{busy === 'gen' ? 'Regenerating…' : 'Regenerate'}</button>
            <button className="primary" onClick={() => download(generated, 'gen-dl')} disabled={busy === 'gen-dl'}>
              {busy === 'gen-dl' ? 'Saving…' : 'Download'}
            </button>
          </div>
        </div>
      )}

      <div className="field">
        <label>Or paste any image URL</label>
        <div className="row" style={{ margin: 0 }}>
          <input
            value={customUrl}
            onChange={e => setCustomUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            style={{ flex: 1 }}
            disabled={busy === 'custom'}
            onKeyDown={e => { if (e.key === 'Enter' && customUrl.trim()) download(customUrl.trim(), 'custom'); }}
          />
          <button onClick={() => download(customUrl.trim(), 'custom')} disabled={busy === 'custom' || !customUrl.trim()}>
            {busy === 'custom' ? 'Downloading…' : 'Download'}
          </button>
        </div>
      </div>

      {savedPath && (
        <div className="status-row status-done" style={{ marginTop: 12 }}>
          <span className="status-icon">✓</span>
          <span>Saved to <code>~/Downloads/{savedPath}</code> — attach it in the X composer.</span>
        </div>
      )}
      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
    </div>
  );
}

// Inline-editable variant. Saves the user's edit to the server on blur (debounced) so future
// generations can learn from how they rewrite AI output.
function VariantCard(props: {
  api: ApiClient;
  draftId: string;
  index: number;
  label?: string;
  text: string;
  originalText: string;
  onChange: (text: string) => void;
}) {
  const [savedHint, setSavedHint] = useState<'idle' | 'saving' | 'saved'>('idle');
  const lastSavedRef = useRef(props.originalText);

  // Auto-grow the textarea to fit content.
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const el = taRef.current; if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.max(el.scrollHeight, 64) + 'px';
  }, [props.text]);

  async function flushEdit() {
    if (!props.draftId) return;
    if (props.text === lastSavedRef.current) return;
    if (props.text.trim() === props.originalText.trim()) return;
    setSavedHint('saving');
    try {
      await props.api.updateDraftAction(props.draftId, 'edited', props.index, props.text);
      lastSavedRef.current = props.text;
      setSavedHint('saved');
      setTimeout(() => setSavedHint('idle'), 1800);
    } catch { setSavedHint('idle'); }
  }

  function copy() {
    navigator.clipboard.writeText(props.text);
    if (props.draftId) {
      // Don't await — best-effort telemetry.
      props.api.updateDraftAction(props.draftId, 'copied', props.index).catch(() => {});
    }
  }
  function post() {
    if (props.draftId) {
      props.api.updateDraftAction(props.draftId, 'posted', props.index).catch(() => {});
    }
    window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(props.text)}`, '_blank');
  }

  const edited = props.text.trim() !== props.originalText.trim();

  return (
    <div className="card variant">
      {props.label && <div className="label">{props.label}{edited && <span className="edited-pill" style={{ marginLeft: 8 }}>edited</span>}</div>}
      <textarea
        ref={taRef}
        className="variant-edit"
        value={props.text}
        onChange={e => props.onChange(e.target.value)}
        onBlur={flushEdit}
        spellCheck
      />
      <div className="button-row end">
        <span className="muted small">
          {props.text.length} chars
          {savedHint === 'saving' && ' · saving edit…'}
          {savedHint === 'saved' && ' · edit saved'}
        </span>
        <button onClick={copy}>Copy</button>
        <button className="primary" onClick={post}>Post on X →</button>
      </div>
    </div>
  );
}
